import logger from '../../utils/logger';
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import {
  LogOut, Plus, Save, Loader2, Upload, FileText, Receipt, Car,
  ClipboardList, X, CheckCircle, Clock, AlertCircle, Eye, User
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EXPENSE_CATEGORIES = [
  "Travel", "Hotel", "Meals", "Supplies", "Events",
  "Food Assistance", "Hygiene Items", "Admin / Operations", "Other"
];

const PAYMENT_METHODS = [
  "Cash", "Credit Card", "Debit Card", "Bank Transfer",
  "Check", "Zelle", "Venmo", "PayPal", "Other"
];

const StatusBadge = ({ status }) => {
  const colors = {
    submitted: 'bg-yellow-100 text-yellow-700',
    approved: 'bg-green-100 text-green-700',
    rejected: 'bg-red-100 text-red-700',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors[status] || 'bg-gray-100 text-gray-600'}`}>
      {status}
    </span>
  );
};

const TeamPortal = () => {
  const { user, logout, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('submissions');
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showExpenseForm, setShowExpenseForm] = useState(false);
  const [showTripForm, setShowTripForm] = useState(false);
  const [receiptFile, setReceiptFile] = useState('');
  const [uploading, setUploading] = useState(false);
  const [peopleInput, setPeopleInput] = useState('');

  const [expenseForm, setExpenseForm] = useState({
    date: new Date().toISOString().split('T')[0], paid_by: '', category: 'Travel',
    description: '', amount: 0, payment_method: 'Cash', reimbursed: 'not_reimbursed',
    funding_source: '', notes: ''
  });

  const [tripForm, setTripForm] = useState({
    date: new Date().toISOString().split('T')[0], destination: '', starting_location: '',
    purpose: '', miles_traveled: 0, people_involved: [],
    visit_notes: '', meeting_notes: '', needs_identified: '', mission_support: '',
    reimbursement_owed: false, notes: ''
  });

  const [submitNotes, setSubmitNotes] = useState('');

  useEffect(() => {
    if (!authLoading && !user) navigate('/admin/team/login');
    else if (!authLoading && user && !['team_member', 'admin'].includes(user.role)) navigate('/');
  }, [user, authLoading, navigate]);

  const fetchSubmissions = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/team/submissions`, { withCredentials: true });
      setSubmissions(res.data);
    } catch (e) { logger.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (user) fetchSubmissions();
  }, [user, fetchSubmissions]);

  const handleReceiptUpload = async (file) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${BACKEND_URL}/api/team/upload-receipt`, formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      setReceiptFile(res.data.filename);
    } catch (e) { alert('Upload failed'); }
    setUploading(false);
  };

  const handleSubmitExpense = async () => {
    if (!expenseForm.date || !expenseForm.description || !expenseForm.amount) return;
    setSaving(true);
    try {
      const payload = {
        submission_type: 'expense',
        expense_data: { ...expenseForm, paid_by: expenseForm.paid_by || user?.name || '' },
        receipt_file: receiptFile,
        notes: submitNotes,
      };
      await axios.post(`${BACKEND_URL}/api/team/submit`, payload, { withCredentials: true });
      setShowExpenseForm(false);
      setExpenseForm({ date: new Date().toISOString().split('T')[0], paid_by: '', category: 'Travel', description: '', amount: 0, payment_method: 'Cash', reimbursed: 'not_reimbursed', funding_source: '', notes: '' });
      setReceiptFile('');
      setSubmitNotes('');
      fetchSubmissions();
    } catch (e) { alert('Submission failed'); }
    setSaving(false);
  };

  const handleSubmitTrip = async () => {
    if (!tripForm.date || !tripForm.destination || !tripForm.purpose) return;
    setSaving(true);
    try {
      const payload = {
        submission_type: 'trip',
        trip_data: tripForm,
        receipt_file: receiptFile,
        notes: submitNotes,
      };
      await axios.post(`${BACKEND_URL}/api/team/submit`, payload, { withCredentials: true });
      setShowTripForm(false);
      setTripForm({ date: new Date().toISOString().split('T')[0], destination: '', starting_location: '', purpose: '', miles_traveled: 0, people_involved: [], visit_notes: '', meeting_notes: '', needs_identified: '', mission_support: '', reimbursement_owed: false, notes: '' });
      setPeopleInput('');
      setReceiptFile('');
      setSubmitNotes('');
      fetchSubmissions();
    } catch (e) { alert('Submission failed'); }
    setSaving(false);
  };

  const handleLogout = async () => { await logout(); navigate('/admin/team/login'); };

  const inputClass = "w-full px-3 py-2 border border-[#E5E7EB] rounded-md text-sm focus:border-[#BA1826] focus:ring-1 focus:ring-[#BA1826]/20 outline-none";
  const labelClass = "block text-xs font-medium text-[#4B5563] mb-1";

  if (authLoading || loading) {
    return <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#BA1826]" /></div>;
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="team-portal">
      {/* Header */}
      <header className="bg-[#0A0A0A] text-white">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FileText className="w-5 h-5 text-[#BA1826]" />
            <div>
              <h1 className="font-heading font-bold text-sm">Team Portal</h1>
              <p className="text-xs text-gray-400">{user?.name} - {user?.email}</p>
            </div>
          </div>
          <button onClick={handleLogout} className="flex items-center gap-1 text-sm text-gray-400 hover:text-white"><LogOut className="w-4 h-4" /> Sign out</button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Action Buttons */}
        <div className="flex flex-wrap gap-3 mb-6">
          <button onClick={() => { setShowExpenseForm(true); setShowTripForm(false); }} className="flex items-center gap-2 px-4 py-3 bg-white border border-[#E5E7EB] rounded-lg hover:border-[#BA1826] transition-colors" data-testid="new-expense-btn">
            <Receipt className="w-5 h-5 text-[#BA1826]" />
            <div className="text-left"><p className="text-sm font-medium">Submit Expense</p><p className="text-xs text-[#9CA3AF]">Log a new expense for review</p></div>
          </button>
          <button onClick={() => { setShowTripForm(true); setShowExpenseForm(false); }} className="flex items-center gap-2 px-4 py-3 bg-white border border-[#E5E7EB] rounded-lg hover:border-[#BA1826] transition-colors" data-testid="new-trip-btn">
            <Car className="w-5 h-5 text-[#BA1826]" />
            <div className="text-left"><p className="text-sm font-medium">Submit Trip</p><p className="text-xs text-[#9CA3AF]">Log a trip report for review</p></div>
          </button>
        </div>

        {/* Expense Form */}
        {showExpenseForm && (
          <div className="bg-white border border-[#E5E7EB] rounded-lg p-6 mb-6" data-testid="team-expense-form">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-heading font-semibold text-sm">Submit Expense for Review</h3>
              <button onClick={() => setShowExpenseForm(false)}><X className="w-5 h-5 text-[#4B5563]" /></button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div><label className={labelClass}>Date *</label><input type="date" value={expenseForm.date} onChange={(e) => setExpenseForm(f => ({ ...f, date: e.target.value }))} className={inputClass} data-testid="team-expense-date" /></div>
              <div><label className={labelClass}>Paid By</label><input type="text" value={expenseForm.paid_by} onChange={(e) => setExpenseForm(f => ({ ...f, paid_by: e.target.value }))} className={inputClass} placeholder={user?.name || 'Your name'} data-testid="team-expense-paid-by" /></div>
              <div><label className={labelClass}>Category</label>
                <select value={expenseForm.category} onChange={(e) => setExpenseForm(f => ({ ...f, category: e.target.value }))} className={inputClass} data-testid="team-expense-category">
                  {EXPENSE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div><label className={labelClass}>Amount ($) *</label><input type="number" step="0.01" value={expenseForm.amount} onChange={(e) => setExpenseForm(f => ({ ...f, amount: parseFloat(e.target.value) || 0 }))} className={inputClass} data-testid="team-expense-amount" /></div>
              <div><label className={labelClass}>Payment Method</label>
                <select value={expenseForm.payment_method} onChange={(e) => setExpenseForm(f => ({ ...f, payment_method: e.target.value }))} className={inputClass} data-testid="team-expense-method">
                  {PAYMENT_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div><label className={labelClass}>Funding Source</label><input type="text" value={expenseForm.funding_source} onChange={(e) => setExpenseForm(f => ({ ...f, funding_source: e.target.value }))} className={inputClass} data-testid="team-expense-funding" /></div>
              <div className="md:col-span-3"><label className={labelClass}>Description *</label><input type="text" value={expenseForm.description} onChange={(e) => setExpenseForm(f => ({ ...f, description: e.target.value }))} className={inputClass} placeholder="What was this expense for?" data-testid="team-expense-desc" /></div>
            </div>

            {/* Receipt Upload */}
            <div className="mt-4 border-t border-[#E5E7EB] pt-4">
              <label className={labelClass}>Upload Receipt</label>
              {receiptFile ? (
                <div className="flex items-center gap-2 text-sm text-green-600"><CheckCircle className="w-4 h-4" /> Receipt uploaded
                  <a href={`${BACKEND_URL}/api/uploads/${receiptFile}`} target="_blank" rel="noreferrer" className="text-[#BA1826] hover:underline">View</a>
                </div>
              ) : (
                <label className="flex items-center gap-2 cursor-pointer text-sm text-[#BA1826] hover:underline" data-testid="team-receipt-upload">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  {uploading ? 'Uploading...' : 'Choose file (JPG, PNG, PDF)'}
                  <input type="file" className="hidden" accept=".jpg,.jpeg,.png,.gif,.pdf,.webp" onChange={(e) => { if (e.target.files[0]) handleReceiptUpload(e.target.files[0]); }} />
                </label>
              )}
            </div>

            <div className="mt-4"><label className={labelClass}>Additional Notes</label><textarea value={submitNotes} onChange={(e) => setSubmitNotes(e.target.value)} className={inputClass} rows={2} placeholder="Anything the admin should know..." data-testid="team-expense-notes" /></div>

            <div className="flex gap-2 mt-4">
              <button onClick={handleSubmitExpense} disabled={saving} className="flex items-center gap-1 px-4 py-2 bg-[#BA1826] text-white rounded-md text-sm hover:bg-[#9A1420] disabled:opacity-50" data-testid="team-submit-expense">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Submit for Review
              </button>
              <button onClick={() => setShowExpenseForm(false)} className="px-4 py-2 border border-[#E5E7EB] rounded-md text-sm hover:bg-[#F1F5F9]">Cancel</button>
            </div>
          </div>
        )}

        {/* Trip Form */}
        {showTripForm && (
          <div className="bg-white border border-[#E5E7EB] rounded-lg p-6 mb-6" data-testid="team-trip-form">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-heading font-semibold text-sm">Submit Trip Report for Review</h3>
              <button onClick={() => setShowTripForm(false)}><X className="w-5 h-5 text-[#4B5563]" /></button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div><label className={labelClass}>Date *</label><input type="date" value={tripForm.date} onChange={(e) => setTripForm(f => ({ ...f, date: e.target.value }))} className={inputClass} data-testid="team-trip-date" /></div>
              <div><label className={labelClass}>Destination *</label><input type="text" value={tripForm.destination} onChange={(e) => setTripForm(f => ({ ...f, destination: e.target.value }))} className={inputClass} data-testid="team-trip-dest" /></div>
              <div><label className={labelClass}>Starting Location</label><input type="text" value={tripForm.starting_location} onChange={(e) => setTripForm(f => ({ ...f, starting_location: e.target.value }))} className={inputClass} data-testid="team-trip-start" /></div>
              <div><label className={labelClass}>Miles Traveled</label><input type="number" step="0.1" value={tripForm.miles_traveled} onChange={(e) => setTripForm(f => ({ ...f, miles_traveled: parseFloat(e.target.value) || 0 }))} className={inputClass} data-testid="team-trip-miles" /></div>
              <div><label className={labelClass}>People Involved</label><input type="text" value={peopleInput} onChange={(e) => { setPeopleInput(e.target.value); setTripForm(f => ({ ...f, people_involved: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })); }} className={inputClass} placeholder="Comma-separated" data-testid="team-trip-people" /></div>
              <div className="flex items-end pb-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={tripForm.reimbursement_owed} onChange={(e) => setTripForm(f => ({ ...f, reimbursement_owed: e.target.checked }))} className="w-4 h-4" data-testid="team-trip-reimb" />
                  <span className="text-sm text-[#4B5563]">Reimbursement Owed</span>
                </label>
              </div>
              <div className="md:col-span-3"><label className={labelClass}>Purpose / Mission *</label><textarea value={tripForm.purpose} onChange={(e) => setTripForm(f => ({ ...f, purpose: e.target.value }))} className={inputClass} rows={2} placeholder="Why was this trip taken?" data-testid="team-trip-purpose" /></div>
              <div className="md:col-span-3"><label className={labelClass}>Visit Notes</label><textarea value={tripForm.visit_notes} onChange={(e) => setTripForm(f => ({ ...f, visit_notes: e.target.value }))} className={inputClass} rows={2} data-testid="team-trip-visit" /></div>
              <div className="md:col-span-3"><label className={labelClass}>Meeting Notes</label><textarea value={tripForm.meeting_notes} onChange={(e) => setTripForm(f => ({ ...f, meeting_notes: e.target.value }))} className={inputClass} rows={2} placeholder="Who did you meet? What was discussed?" data-testid="team-trip-meeting" /></div>
              <div className="md:col-span-3"><label className={labelClass}>Needs Identified</label><textarea value={tripForm.needs_identified} onChange={(e) => setTripForm(f => ({ ...f, needs_identified: e.target.value }))} className={inputClass} rows={2} data-testid="team-trip-needs" /></div>
              <div className="md:col-span-3"><label className={labelClass}>Mission Support</label><textarea value={tripForm.mission_support} onChange={(e) => setTripForm(f => ({ ...f, mission_support: e.target.value }))} className={inputClass} rows={2} data-testid="team-trip-mission" /></div>
            </div>

            <div className="mt-4 border-t border-[#E5E7EB] pt-4">
              <label className={labelClass}>Upload Receipt</label>
              {receiptFile ? (
                <div className="flex items-center gap-2 text-sm text-green-600"><CheckCircle className="w-4 h-4" /> Receipt uploaded
                  <a href={`${BACKEND_URL}/api/uploads/${receiptFile}`} target="_blank" rel="noreferrer" className="text-[#BA1826] hover:underline">View</a>
                </div>
              ) : (
                <label className="flex items-center gap-2 cursor-pointer text-sm text-[#BA1826] hover:underline">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  {uploading ? 'Uploading...' : 'Choose file'}
                  <input type="file" className="hidden" accept=".jpg,.jpeg,.png,.gif,.pdf,.webp" onChange={(e) => { if (e.target.files[0]) handleReceiptUpload(e.target.files[0]); }} />
                </label>
              )}
            </div>

            <div className="mt-4"><label className={labelClass}>Additional Notes</label><textarea value={submitNotes} onChange={(e) => setSubmitNotes(e.target.value)} className={inputClass} rows={2} data-testid="team-trip-notes" /></div>

            <div className="flex gap-2 mt-4">
              <button onClick={handleSubmitTrip} disabled={saving} className="flex items-center gap-1 px-4 py-2 bg-[#BA1826] text-white rounded-md text-sm hover:bg-[#9A1420] disabled:opacity-50" data-testid="team-submit-trip">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Submit for Review
              </button>
              <button onClick={() => setShowTripForm(false)} className="px-4 py-2 border border-[#E5E7EB] rounded-md text-sm hover:bg-[#F1F5F9]">Cancel</button>
            </div>
          </div>
        )}

        {/* My Submissions */}
        <div>
          <h2 className="font-heading font-semibold text-sm mb-4 flex items-center gap-2">
            <ClipboardList className="w-4 h-4 text-[#BA1826]" /> My Submissions
          </h2>
          {submissions.length === 0 ? (
            <div className="bg-white border border-[#E5E7EB] rounded-lg p-8 text-center text-sm text-[#9CA3AF]">
              No submissions yet. Use the buttons above to submit an expense or trip report.
            </div>
          ) : (
            <div className="space-y-3">
              {submissions.map((s) => (
                <div key={s.id} className="bg-white border border-[#E5E7EB] rounded-lg p-4" data-testid={`team-submission-${s.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xs bg-[#F1F5F9] px-2 py-0.5 rounded capitalize font-medium">{s.submission_type}</span>
                      <StatusBadge status={s.status} />
                    </div>
                    <span className="text-xs text-[#9CA3AF]">{new Date(s.created_at).toLocaleDateString()}</span>
                  </div>
                  {s.submission_type === 'expense' && s.expense_data && (
                    <div className="text-sm">
                      <p className="font-medium">{s.expense_data.description}</p>
                      <p className="text-[#4B5563] text-xs mt-1">${s.expense_data.amount} - {s.expense_data.category} - {s.expense_data.date}</p>
                    </div>
                  )}
                  {s.submission_type === 'trip' && s.trip_data && (
                    <div className="text-sm">
                      <p className="font-medium">{s.trip_data.destination}</p>
                      <p className="text-[#4B5563] text-xs mt-1">{s.trip_data.purpose} - {s.trip_data.date}</p>
                    </div>
                  )}
                  {s.receipt_file && (
                    <a href={`${BACKEND_URL}/api/uploads/${s.receipt_file}`} target="_blank" rel="noreferrer" className="text-[#BA1826] text-xs hover:underline flex items-center gap-1 mt-2"><Eye className="w-3 h-3" /> View Receipt</a>
                  )}
                  {s.reviewed_by && (
                    <div className="text-xs text-[#9CA3AF] border-t border-[#E5E7EB] pt-2 mt-2">
                      {s.status === 'approved' && <CheckCircle className="w-3 h-3 text-green-500 inline mr-1" />}
                      {s.status === 'rejected' && <AlertCircle className="w-3 h-3 text-red-500 inline mr-1" />}
                      Reviewed by {s.reviewed_by} on {new Date(s.reviewed_at).toLocaleDateString()}
                      {s.review_notes && <span className="block mt-1">"{s.review_notes}"</span>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TeamPortal;
